import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "@stadium/services/auth0";
import { selectAuth } from "@stadium/redux-utils/selectors";
import { authActions } from "@stadium/redux-utils/slices";
import { userCookie } from "@stadium/helpers/cookie";
import { client, getAuthLink } from "@services/apollo-client";

import { axiosInstance } from "@services/axios";

import Loaders from "@stadium/ui/src/components/loaders/loaders";

const AUTHORIZATION_HEADER = "X-Authorization";

const AuthWrapper = ({ children }) => {
  const { getAccessTokenSilently, isAuthenticated, isLoading, user } = useAuth0();

  const dispatch = useDispatch();
  const [authLoaded, setAuthLoaded] = useState(false);
  const cookieUser = userCookie.get();
  const cookieAuthenticated = cookieUser?.authenticated;
  const { authenticated: storeAuthenticated } = useSelector(selectAuth);
  const shouldRenderChildren = authLoaded || (!isLoading && !isAuthenticated);

  useEffect(() => {
    if (user) {
      dispatch(authActions.initUser(user));
    }
  }, [dispatch, user]);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const addToken = async () => {
        try {
          const { access_token: token, expires_in: expiresIn } = await getAccessTokenSilently({
            detailedResponse: true,
          });
          if (token) {
            client.setLink(getAuthLink(token));
            axiosInstance.addHeader(AUTHORIZATION_HEADER, `Bearer ${token}`);
            setTimeout(() => {
              addToken();
            }, (expiresIn - 10) * 1000);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err);
        } finally {
          setAuthLoaded(true);
        }
      };
      addToken();
    }
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  // This is done so that the auth0 access token key is removed from the memory when the user logs out from the other tab.
  // Auth0 maintains different access token for each tab.
  useEffect(() => {
    if (storeAuthenticated && ((!isAuthenticated && !isLoading) || !cookieAuthenticated)) {
      window.location.reload();
    }
  }, [isAuthenticated, storeAuthenticated, isLoading, cookieAuthenticated]);

  return shouldRenderChildren ? children : <Loaders.PageLoader />;
};

export default AuthWrapper;
